import { useState, type ReactNode } from 'react';
import { fmtNum, fmtTRY, SkeletonLoader } from '../../yeni-design/primitives';
import { DeltaHucre } from '../DeltaHucre';
import { miktarMetni, oranMetni } from '../bicim';

/** Detay govdelerinin ortak parcalari (G2b/K7). Testid'ler cagiranin govde adiyla baslar. */

/** `null`/`undefined` kontrolu — ozet ve tablo hesaplarinda tekrarlanmasin diye TEK yerde (D-FE4). */
export const bos = (x: unknown): x is null | undefined => x === null || x === undefined;

export function DetayYukleniyor({ testid }: { testid: string }) {
  return (
    <div data-testid={testid} aria-busy="true" aria-live="polite" className="py-2">
      <SkeletonLoader lines={5} lineClass="h-4" />
    </div>
  );
}

export function DetayMesaj({ testid, children }: { testid: string; children: ReactNode }) {
  return (
    <p data-testid={testid} className="text-[13px] text-ink-500 py-3">
      {children}
    </p>
  );
}

export function DetayBolum({ baslik, testid, children }: { baslik: string; testid?: string; children: ReactNode }) {
  return (
    <section data-testid={testid} className="flex flex-col gap-2 min-w-0">
      <h3 className="text-[13px] font-semibold text-ink-900">{baslik}</h3>
      {children}
    </section>
  );
}

export interface OzetKutusu {
  testid: string;
  etiket: string;
  deger: string;
  ek?: ReactNode;
}

export function OzetKutulari({ kutular }: { kutular: OzetKutusu[] }) {
  return (
    <dl className="grid grid-cols-2 sm:grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-3">
      {kutular.map((k) => (
        <div key={k.testid} data-testid={k.testid} className="border border-border rounded-md px-3 py-2 min-w-0">
          <dt className="text-[11px] text-ink-500">{k.etiket}</dt>
          <dd className="text-[15px] font-semibold text-ink-900 tnum truncate">{k.deger}</dd>
          {k.ek !== undefined && <dd className="text-[11px] text-ink-500 tnum mt-0.5">{k.ek}</dd>}
        </div>
      ))}
    </dl>
  );
}

/** Δ hucresi TEK bilesen (`../DeltaHucre.tsx`, D-FE1 duzeltmesi); detay govdeleri buradan almaya devam eder. */
export { DeltaHucre };

export interface CubukNoktasi {
  anahtar: string;
  etiket: string;
  deger: number;
  /** Tam metin: fareyle anlik ipucu ve ekran okuyucu. */
  baslik: string;
  /** S295: cubugun ustunde yazan kisa deger ("412 B", "3,75"). Verilmezse etiket yok. */
  kisa?: string;
}

/** Bundan cok cubukta etiket dikey yazilir: aylik 31 cubukta yatay "1,55 Mn" cubuk genisligine sigmaz. */
export const YATAY_ETIKET_EN_COK = 16;
/**
 * S298: bundan cok cubukta etiket HIC yazilmaz, yalniz ipucu kalir. Canlida olculdu (2026-09-24): Yillik donemde
 * kanal modali 148 cubuk ciziyordu (3,7 px); 148 dikey etiket ust uste binip okunmuyordu. 31 = bir ayin gunleri.
 */
export const ETIKET_EN_COK = 31;

/** Yuvarlak adim carpanlari (x 10^n): Y ekseni 0 · 400.000 · 800.000 gibi okunur degerler basar. */
const ADIM_CARPANLARI = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];
const yuvarla = (x: number) => Number(x.toPrecision(12));

/**
 * Y ekseni olcegi: en buyuk MUTLAK degeri 4 esit yuvarlak adima boler; `ust` = 4 adim (cubuk boylari buna gore,
 * eksen cizgileriyle hizali). En buyuk 0 ise olcek yok.
 */
export function eksenOlcegi(enBuyuk: number): { ust: number; adimlar: number[] } {
  if (!(enBuyuk > 0)) return { ust: 0, adimlar: [] };
  const kaba = enBuyuk / 4;
  const us = 10 ** Math.floor(Math.log10(kaba));
  const adim = ADIM_CARPANLARI.map((c) => yuvarla(c * us)).find((a) => a >= kaba) ?? yuvarla(10 * us);
  return { ust: yuvarla(adim * 4), adimlar: [0, 1, 2, 3, 4].map((i) => yuvarla(adim * i)) };
}

/**
 * Gunluk/saatlik cubuklar — recharts DEGIL (SOZLESME SAPMASI 6): antd Modal acilis animasyonunda
 * ResponsiveContainer 0 genislik olcer; jsdom'da da cizmez. Negatif (iade agirlikli) gun kirmizi.
 * Sayilar GORUNUR (kullanici 2026-09-15: "sadece grafik var, sayilar da gorunmeli"): sayfadaki Ciro trendi gibi
 * solda Y ekseni + her adimda yatay cizgi; cubuk boyu eksen ust sinirina gore.
 * S295 (kullanici 2026-09-23): cubugun ustunde kisa deger (`kisa`; 0'da yok) ve fareyle ANLIK ipucu (tam deger).
 * Tarayicinin `title`i gec aciliyordu ve ikisi ust uste binerdi; kaldirildi, ayni metin `aria-label`da.
 * Etiket icin grafigin ustunde pay birakilir (en uzun cubugun etiketi karta tasmasin).
 */
export function GunlukCubuklar({ testid, noktalar }: { testid: string; noktalar: CubukNoktasi[] }) {
  const [aktif, setAktif] = useState<string | null>(null);
  const enBuyuk = Math.max(0, ...noktalar.map((n) => Math.abs(n.deger)));
  const { ust, adimlar } = eksenOlcegi(enBuyuk);
  const konum = (a: number) => ({ bottom: `${(a / ust) * 100}%` });
  const etiketli = noktalar.length <= ETIKET_EN_COK;
  const etiketVar = etiketli && noktalar.some((n) => n.kisa !== undefined && n.deger !== 0);
  const dikey = noktalar.length > YATAY_ETIKET_EN_COK;
  const pay = !etiketVar ? '' : dikey ? ' pt-12' : ' pt-5';
  return (
    <div data-testid={testid} className="flex flex-col gap-1">
      <div className={`flex gap-2${pay}`}>
        {adimlar.length > 0 && (
          <div
            data-testid={`${testid}-eksen`}
            aria-hidden="true"
            className="relative w-16 shrink-0 h-[140px] text-[11px] leading-none text-ink-500 tnum"
          >
            {adimlar.map((a) => (
              <span key={a} className="absolute right-0 translate-y-1/2" style={konum(a)}>
                {fmtNum(a)}
              </span>
            ))}
          </div>
        )}
        <div className="relative flex-1 min-w-0 h-[140px]">
          {adimlar.map((a) => (
            <div
              key={a}
              data-testid={`${testid}-cizgi`}
              aria-hidden="true"
              className="absolute inset-x-0 border-t border-dashed border-border"
              style={konum(a)}
            />
          ))}
          <div className="relative flex items-end gap-[2px] h-full">
            {noktalar.map((n, i) => {
              const yuzde = ust > 0 && n.deger !== 0 ? Math.max(1, (Math.abs(n.deger) / ust) * 100) : 0;
              // Ipucu kenar cubuklarda iceri hizalanir: ortalanmis kutu ilk/son cubukta karttan tasardi.
              // Ipucu cubugun (ve varsa etiketinin) HEMEN ustunde: grafigin tepesinde olsa alcak cubuktan uzak kalirdi.
              const etiketGoster = etiketli && n.kisa !== undefined && n.deger !== 0;
              const etiketPayi = !etiketGoster ? 4 : dikey ? 48 : 16;
              const hiza = i < noktalar.length / 3 ? 'left-0' : i >= (noktalar.length * 2) / 3 ? 'right-0' : 'left-1/2 -translate-x-1/2';
              return (
                <div
                  key={n.anahtar}
                  data-testid={`${testid}-${n.anahtar}`}
                  role="img"
                  aria-label={n.baslik}
                  onMouseEnter={() => setAktif(n.anahtar)}
                  onMouseLeave={() => setAktif((a) => (a === n.anahtar ? null : a))}
                  className="relative flex-1 min-w-[3px] h-full flex items-end"
                >
                  <div
                    className={`w-full rounded-t-[2px] ${n.deger < 0 ? 'bg-neg-600' : 'bg-ink-700'}${aktif === n.anahtar ? ' opacity-80' : ''}`}
                    style={{ height: `${yuzde}%` }}
                  />
                  {etiketGoster && (
                    <span
                      data-testid={`${testid}-${n.anahtar}-deger`}
                      data-yon={dikey ? 'dikey' : 'yatay'}
                      aria-hidden="true"
                      className={`absolute left-1/2 -translate-x-1/2 mb-0.5 whitespace-nowrap text-[10px] leading-none text-ink-700 tnum pointer-events-none${dikey ? ' [writing-mode:vertical-rl] rotate-180' : ''}`}
                      style={{ bottom: `${yuzde}%` }}
                    >
                      {n.kisa}
                    </span>
                  )}
                  {aktif === n.anahtar && (
                    <span
                      data-testid={`${testid}-ipucu`}
                      aria-hidden="true"
                      className={`absolute ${hiza} z-10 whitespace-nowrap rounded bg-ink-900 px-2 py-1 text-[11px] text-white tnum shadow pointer-events-none`}
                      style={{ bottom: `calc(${yuzde}% + ${etiketPayi}px)` }}
                    >
                      {n.baslik}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {noktalar.length > 0 && (
        <div
          data-testid={`${testid}-etiketler`}
          className={`flex justify-between text-[11px] text-ink-500 tnum${adimlar.length > 0 ? ' pl-[4.5rem]' : ''}`}
        >
          <span>{noktalar[0].etiket}</span>
          <span>{noktalar[noktalar.length - 1].etiket}</span>
        </div>
      )}
    </div>
  );
}

/** Istemci aramasi: Turkce kucuk harfe cevirip alanlardan BIRINDE gecen satiri birakir. Bos metin kimliktir. */
export function aramaSuz<T>(rows: T[], metin: string, alanlar: (r: T) => string[]): T[] {
  const m = metin.trim().toLocaleLowerCase('tr');
  if (m === '') return rows;
  return rows.filter((r) => alanlar(r).some((a) => a.toLocaleLowerCase('tr').includes(m)));
}

/**
 * D-FE3/D-FE4 (kontrolor kurali): KategoriDetay ve ZararDetay'in arama <input>'u brief'te VERBATIM
 * tekrarlaniyordu — tek bilesene tasindi. testid/placeholder/aria/davranis GOVDELERDE AYNI kalir.
 */
export function AramaKutusu({
  testid,
  placeholder,
  ariaLabel,
  value,
  onChange,
}: {
  testid: string;
  placeholder: string;
  ariaLabel?: string;
  value: string;
  onChange: (deger: string) => void;
}) {
  return (
    <input
      type="search"
      data-testid={testid}
      aria-label={ariaLabel ?? placeholder}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-8 w-full sm:w-64 bg-white border border-border rounded-md px-3 text-[13px] text-ink-900 placeholder:text-ink-400 outline-none focus:border-brand-600"
    />
  );
}

export interface DagilimSatiri {
  /** Satir testid soneki ve gorunen ad (birebir metin). */
  ad: string;
  /** Ad'dan sonra " · <ek>" olarak basilir (orn. kanal adindan sonra grubu). */
  ek?: string;
  ciro: number;
  /** Onceden hesaplanmis pay (payHesapla); toplam bu bilesende TUTULMAZ — cagiran hangi taban uzerinden
   * (kendi toplami mi, ustteki oz.ciro mu) hesaplayacagini bilir (D-FE5). */
  pay: number | null;
  /** Sag ucdaki sayac kolonu — Fiş (M2/M3 kanal, M4) ya da Miktar (M3 kategori). */
  sayac: number;
  /** Verilirse (ve `deltaGoster`) satir sonuna Δ ciro kolonu eklenir; null "—" basar. */
  oncekiCiro?: number | null;
}

/**
 * G2b/C8 (D-FE5, kontrolor kurali): KanalDetay/SubeDetay/GunDetay ayni sekilde bir dagilim tablosu
 * ciziyordu (baslik + Ciro/Pay/<sayac>[/Δ ciro] kolonlari, satir testid'i `<onek>-<ad>`). TEK bilesende:
 * testid/kolon basligi/hucre metni her cagiranin testinde beklenenle BIREBIR ayni kalir.
 */
export function DagilimTablosu({
  testid,
  satirTestid,
  baslikSutunu,
  sayacBasligi = 'Fiş',
  deltaGoster = false,
  satirlar,
}: {
  testid: string;
  satirTestid: string;
  baslikSutunu: string;
  sayacBasligi?: string;
  deltaGoster?: boolean;
  satirlar: DagilimSatiri[];
}) {
  const sayacSinifi = `py-2 text-right tnum text-ink-500 ${deltaGoster ? 'px-3' : 'pl-3'}`;
  return (
    <div className="overflow-x-auto">
      <table data-testid={testid} className="w-full text-[13px] border-collapse">
        <thead>
          <tr className="text-[12px] text-ink-500 border-b border-border">
            <th className="text-left font-medium py-2 pr-3">{baslikSutunu}</th>
            <th className="text-right font-medium py-2 px-3">Ciro</th>
            <th className="text-right font-medium py-2 px-3">Pay</th>
            <th className={`text-right font-medium py-2 ${deltaGoster ? 'px-3' : 'pl-3'}`}>{sayacBasligi}</th>
            {deltaGoster && <th className="text-right font-medium py-2 pl-3">Δ ciro</th>}
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s) => (
            <tr key={`${s.ad}|${s.ek ?? ''}`} data-testid={`${satirTestid}-${s.ad}`} className="border-b border-border last:border-b-0">
              <td className="py-2 pr-3 text-ink-900">
                {s.ad}
                {s.ek && <span className="text-ink-500"> · {s.ek}</span>}
              </td>
              <td className="py-2 px-3 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(s.ciro)}</td>
              <td className="py-2 px-3 text-right tnum text-ink-500">{s.pay === null ? '—' : oranMetni(s.pay, 1)}</td>
              <td className={sayacSinifi}>{miktarMetni(s.sayac)}</td>
              {deltaGoster && (
                <td className="py-2 pl-3 text-right whitespace-nowrap">
                  <DeltaHucre bu={s.ciro} onceki={s.oncekiCiro ?? null} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
